/**
 * The copyright in this software is being made available under the BSD License,
 * included below. This software may be subject to other third party and contributor
 * rights, including patent rights, and no such rights are granted under this license.
 *
 * Copyright (c) 2013, Dash Industry Forum.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *  * Redistributions of source code must retain the above copyright notice, this
 *  list of conditions and the following disclaimer.
 *  * Redistributions in binary form must reproduce the above copyright notice,
 *  this list of conditions and the following disclaimer in the documentation and/or
 *  other materials provided with the distribution.
 *  * Neither the name of Dash Industry Forum nor the names of its
 *  contributors may be used to endorse or promote products derived from this software
 *  without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS AS IS AND ANY
 *  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 *  WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 *  IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 *  INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 *  NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 *  PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 *  ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 *  POSSIBILITY OF SUCH DAMAGE.
 */

import BaseURLTreeModel from '../models/BaseURLTreeModel.js';
import BaseURLSelector from '../utils/BaseURLSelector.js';
import URLUtils from '../utils/URLUtils.js';
import BaseURL from '../../dash/vo/BaseURL.js';
import FactoryMaker from '../../core/FactoryMaker.js';
import EventBus from '../../core/EventBus.js';
import Events from '../../core/events/Events.js';
import MediaPlayerEvents from '../MediaPlayerEvents.js';

function BaseURLController() {

    let instance,
        adapter;

    const context = this.context;
    const eventBus = EventBus(context).getInstance();
    const urlUtils = URLUtils(context).getInstance();

    let baseURLTreeModel,
        baseURLSelector,
        contentSteeringController;

    function onBlackListChanged(e) {
        baseURLTreeModel.invalidateSelectedIndexes(e.entry);
    }

    function setup() {
        baseURLTreeModel = BaseURLTreeModel(context).create();
        baseURLSelector = BaseURLSelector(context).create();
    }

    function setConfig(config) {
        if (config.baseURLTreeModel) {
            baseURLTreeModel = config.baseURLTreeModel;
        }

        if (config.baseURLSelector) {
            baseURLSelector = config.baseURLSelector;
        }

        if (config.adapter) {
            adapter = config.adapter;
        }

        if (config.contentSteeringController) {
            contentSteeringController = config.contentSteeringController
        }
    }

    function update(manifest) {
        baseURLTreeModel.update(manifest);
        baseURLSelector.chooseSelector(adapter.getIsDVB(manifest));
        eventBus.trigger(MediaPlayerEvents.BASE_URLS_UPDATED, {
            baseUrls: getBaseUrlsForEventPayload(manifest)
        });
    }

    function getBaseUrlsForEventPayload(manifest) {
        // Reuse the BaseURLs the tree model already collected during update() instead of
        // re-extracting them from every manifest node.
        const { rootBaseUrls, childBaseUrls } = baseURLTreeModel.getBaseUrlsForPayload();
        const uniqueBaseUrls = [];
        const addedBaseUrls = {};

        const includeRootBaseUrls = _shouldIncludeRootBaseUrls(manifest, childBaseUrls);
        const targetBaseUrls = includeRootBaseUrls ? rootBaseUrls.concat(childBaseUrls) : childBaseUrls;

        targetBaseUrls.forEach((baseUrl) => {
            const key = `${baseUrl.serviceLocation}_${baseUrl.url}`;
            if (!addedBaseUrls[key]) {
                uniqueBaseUrls.push(baseUrl);
                addedBaseUrls[key] = true;
            }
        });

        return uniqueBaseUrls;
    }

    function _shouldIncludeRootBaseUrls(manifest, childBaseUrls) {
        if (!manifest || manifest.BaseURL || childBaseUrls.length === 0) {
            return true;
        }

        if (childBaseUrls.some((baseUrl) => urlUtils.isRelative(baseUrl.url))) {
            return true;
        }

        if (!manifest.Period || manifest.Period.length === 0) {
            return true;
        }

        return manifest.Period.some((period) => _usesInheritedBaseUrl(period));
    }

    function _usesInheritedBaseUrl(element) {
        if (!element || element.BaseURL) {
            return false;
        }

        const children = element.AdaptationSet || element.Representation;
        if (!children || children.length === 0) {
            return true;
        }

        return children.some((child) => _usesInheritedBaseUrl(child));
    }

    function resolve(path) {
        const baseUrls = baseURLTreeModel.getForPath(path);

        const baseUrl = baseUrls.reduce((p, c) => {
            const b = baseURLSelector.select(c);

            if (b) {
                if (!urlUtils.isRelative(b.url)) {
                    p.url = b.url;
                    p.serviceLocation = b.serviceLocation;
                } else {
                    p.url = urlUtils.resolve(b.url, p.url);
                }
                p.availabilityTimeOffset = b.availabilityTimeOffset;
                p.availabilityTimeComplete = b.availabilityTimeComplete;
                p.queryParams = b.queryParams;
            } else {
                return new BaseURL();
            }

            return p;
        }, new BaseURL());

        if (!urlUtils.isRelative(baseUrl.url)) {
            return baseUrl;
        }
    }

    function reset() {
        eventBus.off(Events.SERVICE_LOCATION_BASE_URL_BLACKLIST_CHANGED, onBlackListChanged, instance);
        baseURLTreeModel.reset();
        baseURLSelector.reset();
    }

    function getBaseUrls(manifest) {
        return baseURLTreeModel.getAvailableBaseUrlsForElement(manifest);
    }

    function initialize(data) {
        eventBus.on(Events.SERVICE_LOCATION_BASE_URL_BLACKLIST_CHANGED, onBlackListChanged, instance);
        // report config to baseURLTreeModel and baseURLSelector
        baseURLTreeModel.setConfig({
            adapter,
            contentSteeringController
        });

        baseURLSelector.initialize();

        update(data);
    }

    instance = {
        getBaseUrls,
        initialize,
        reset,
        resolve,
        setConfig,
        update
    };

    setup();

    return instance;
}

BaseURLController.__dashjs_factory_name = 'BaseURLController';
export default FactoryMaker.getClassFactory(BaseURLController);
